import { locales, defaultLocale, routes } from '~/locales/routes.json';
import type { Locale } from '~/utils/language';

type RouteKey = keyof (typeof routes)['cs'];

// Build reverse map at module load time: { locale: { slug: routeKey } }
// This enables O(1) lookup instead of O(n) iteration
const reverseSlugMap: Record<string, Record<string, RouteKey>> = {};

for (const locale of locales) {
  reverseSlugMap[locale] = {};
  const localeRoutes = routes[locale as keyof typeof routes];
  for (const key in localeRoutes) {
    const slug = localeRoutes[key as keyof typeof localeRoutes];
    reverseSlugMap[locale][slug] = key as RouteKey;
  }
}

// Slugs can span several segments (e.g. `objednavka/krok-2`, `ucet/prehled`), so match the
// longest leading run of segments first; whatever follows is the route's params.
function matchSlug(locale: string, segments: string[]) {
  const slugMap = reverseSlugMap[locale];
  if (!slugMap) return null;

  for (let length = segments.length; length >= 1; length--) {
    const slug = segments.slice(0, length).join('/');
    const key = slugMap[slug];
    if (key) {
      return { key, slug, params: segments.slice(length) };
    }
  }

  // The homepage slug is empty — only a bare path (or locale prefix alone) may match it.
  const homepageKey = segments.length === 0 ? slugMap[''] : undefined;
  return homepageKey ? { key: homepageKey, slug: '', params: [] } : null;
}

export function detectRouteKeyFromPathname(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);

  // Check for language prefix
  let locale: Locale = defaultLocale as Locale;
  let slugStart = 0;

  if (locales.includes(segments[0])) {
    locale = segments[0] as Locale;
    slugStart = 1;
  }

  const match = matchSlug(locale, segments.slice(slugStart));
  if (match) {
    return { ...match, locale };
  }

  return null; // unknown route
}

export function convertPathToLanguage(
  pathname: string,
  targetLocale: Locale,
  slugTranslations?: Record<string, string> | null
): string {
  const info = detectRouteKeyFromPathname(pathname);

  // Unknown route (e.g., CMS pages) → use slugTranslations if available, otherwise just add/remove prefix
  if (!info) {
    // If we have slug translations, use the translated slug for the target locale
    if (slugTranslations) {
      const translatedSlug = slugTranslations[targetLocale];
      if (translatedSlug) {
        return targetLocale === defaultLocale ? `/${translatedSlug}` : `/${targetLocale}/${translatedSlug}`;
      }
    }

    // Fallback: add/remove prefix only. The prefix must be a whole segment — `\b` would
    // also match before a hyphen (`/en-primeur`).
    const localePattern = new RegExp(`^/(${locales.join('|')})(?=/|$)`);
    const noPrefix = pathname.replace(localePattern, '');
    return targetLocale === defaultLocale ? noPrefix : `/${targetLocale}${noPrefix}`;
  }

  const { key, params } = info;

  // Build target slug. Target locale (or this route in it) has no slug definition —
  // fall back to the locale's homepage instead of `/undefined`.
  const targetRoutes = routes[targetLocale as keyof typeof routes];
  const newSlug: string | undefined = targetRoutes?.[key as keyof typeof targetRoutes];
  if (newSlug === undefined) {
    return targetLocale === defaultLocale ? '/' : `/${targetLocale}`;
  }

  // Build new base path. Empty slug = homepage; avoid a trailing slash on the locale prefix (`/en/`).
  const base = newSlug ? `/${newSlug}` : '';

  // Translate params if slugTranslations are provided
  let paramPart = '';
  if (slugTranslations) {
    // slugTranslations contains the full translated slug (e.g., "jeans/women")
    const translatedSlug = slugTranslations[targetLocale];
    if (translatedSlug) {
      paramPart = `/${translatedSlug}`;
    }
  } else if (params.length > 0) {
    // No translations, keep original params
    paramPart = `/${params.join('/')}`;
  }

  // Prepend locale prefix for non-default locales
  return targetLocale === defaultLocale ? `${base}${paramPart}` || '/' : `/${targetLocale}${base}${paramPart}`;
}
